/**
 * Picking a user or a group — the share dialog, group members, role assignees,
 * ownership transfer, a rule's audience. For several at once, see
 * `SubjectMultiPicker`; for the search behind it, `hooks/useSubjectSearch`.
 *
 * `RecordPicker` with a subject's key, avatar and caption line filled in, and
 * its labels defaulted to "User or group" / "Search by name or email". Every
 * behaviour — controlled, fetch-free, the current value always offered, the
 * `slotProps` merge — is `RecordPicker`'s, so the two cannot drift.
 *
 * `email` arrives only for a caller who also holds `user.read`, so the secondary
 * line has to cope with it being absent — that is a permission difference, not a
 * missing address.
 */
import { useTranslation } from 'react-i18next';
import type { SubjectDto } from '@forsys/shared';
import { subjectCaption, subjectKey } from '../utils/subject';
import { RecordPicker } from './RecordPicker';
import { SubjectAvatar } from './SubjectAvatar';

export interface SubjectPickerProps {
  /** What the directory search returned for the current query. */
  options: SubjectDto[];
  value: SubjectDto | null;
  loading?: boolean;
  /** Defaults to the translated "User or group". */
  label?: string;
  /** Defaults to the translated "Search by name or email". */
  placeholder?: string;
  helperText?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  onChange: (subject: SubjectDto | null) => void;
  /** Raises on every keystroke. Debounce it in the page, not here. */
  onQueryChange: (query: string) => void;
}

export function SubjectPicker({
  options,
  value,
  loading = false,
  label,
  placeholder,
  helperText,
  disabled = false,
  autoFocus = false,
  onChange,
  onQueryChange,
}: SubjectPickerProps) {
  const { t } = useTranslation('common');

  return (
    <RecordPicker<SubjectDto>
      options={options}
      value={value}
      getKey={subjectKey}
      getLabel={(subject) => subject.displayName}
      getCaption={subjectCaption}
      getIcon={(subject) => <SubjectAvatar type={subject.type} />}
      loading={loading}
      label={label ?? t('subject.userOrGroup')}
      placeholder={placeholder ?? t('subject.searchByNameOrEmail')}
      {...(helperText ? { helperText } : {})}
      noOptionsText={t('subject.noMatches')}
      disabled={disabled}
      autoFocus={autoFocus}
      onChange={onChange}
      onQueryChange={onQueryChange}
    />
  );
}
